/**
 * Convert an ISO datetime string into the value shape an HTML
 * `<input type="datetime-local">` accepts, i.e. `YYYY-MM-DDTHH:mm`
 * in the browser's local timezone. Returns an empty string for null,
 * undefined, empty, or unparseable input — convenient for binding to
 * RHF's `defaultValues`/`reset` where `null` would render `null` in
 * the input.
 *
 * Pair with `new Date(value).toISOString()` on submit to roundtrip
 * back to the API's tz-stable storage.
 */
export function toLocalDateTime(iso?: string | null): string {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    const off = d.getTimezoneOffset();
    return new Date(d.getTime() - off * 60_000).toISOString().slice(0, 16);
}

// ---- Local-calendar date ranges (YYYY-MM-DD, as <input type="date"> emits) ----

export type DateRange = { rangeStart: string; rangeEnd: string };

export const isoDate = (d: Date): string =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

export function defaultRange(): DateRange {
    const now = new Date();
    // Past 12 months: the first day of the month 11 months ago → today.
    return { rangeStart: isoDate(new Date(now.getFullYear(), now.getMonth() - 11, 1)), rangeEnd: isoDate(now) };
}

export function yearToDateRange(): DateRange {
    const now = new Date();
    return { rangeStart: isoDate(new Date(now.getFullYear(), 0, 1)), rangeEnd: isoDate(now) };
}

// Today included, so n=7 means today + the 6 days before it.
export function lastNDaysRange(n: number): DateRange {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - (n - 1));
    return { rangeStart: isoDate(start), rangeEnd: isoDate(now) };
}

// Every YYYY-MM-DD from rangeStart to rangeEnd inclusive.
export function dayBuckets(rangeStart: string, rangeEnd: string): string[] {
    const days: string[] = [];
    const d = new Date(`${rangeStart}T00:00:00`);
    const end = new Date(`${rangeEnd}T00:00:00`);
    while (d <= end) {
        days.push(isoDate(d));
        d.setDate(d.getDate() + 1);
    }
    return days;
}
